import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { ViewStyle } from 'react-native'

import { addDays, getDay, isBefore, isFuture, isValid, isWithinInterval, parse, startOfDay } from 'date-fns'
import { format } from 'date-fns-tz'
import styled from 'styled-components/native'

import { DAYS_OF_WEEK_SUNDAY_FIRST } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/availabilityCalendar/utils'

import { DateField } from '../../atoms/formElements/DateField.web'

const DatePickerContainer = styled.View({ flexGrow: 1, zIndex: 1 })

type ProviderCalendarDateInputProps = {
  value: string | null
  error?: string | null
  onChange: (formattedValue: string) => void
  onValidityChange?: (valid: boolean) => void
  useBuiltInValidation?: boolean
  onFocus?: () => void
  onBlur?: () => void
  style?: ViewStyle
  preventOpenOnFocus?: boolean
  /** Days to prevent from being selected (0=Sunday, 6=Saturday) */
  hiddenDays?: number[]
}

export const ProviderCalendarDateInput: React.FC<ProviderCalendarDateInputProps> = ({
  value,
  onChange,
  onValidityChange,
  error,
  useBuiltInValidation = true,
  onFocus,
  onBlur,
  style,
  preventOpenOnFocus,
  hiddenDays,
}) => {
  const { formatMessage } = useIntl()
  const locale = Intl.DateTimeFormat().resolvedOptions().locale

  const localeDateFormat = useMemo(() => {
    const currentDate = new Date()
    const formatter = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
    const parts = formatter.formatToParts(currentDate)
    return parts
      .map((p) => (p.type === 'day' ? 'dd' : p.type === 'month' ? 'MM' : p.type === 'year' ? 'yyyy' : p.value))
      .join('')
  }, [locale])

  const [isValidDateSelection, setIsValidDateSelection] = useState(true)
  const [dateInvalidError, setDateInvalidError] = useState<string | null>(null)

  const validate = useCallback(
    (formatted: string | null, emitChange: boolean) => {
      if (!formatted) {
        return
      }
      if (!useBuiltInValidation) {
        onChange(formatted)
        return
      }
      const parsed = parse(formatted, localeDateFormat, new Date())
      const today = startOfDay(new Date())
      const futureCutoff = addDays(today, 91)
      const dayOfWeekNum = getDay(parsed)
      const isHiddenDay = hiddenDays && hiddenDays.includes(dayOfWeekNum)
      const valid =
        isValid(parsed) &&
        isWithinInterval(parsed, { start: today, end: futureCutoff }) &&
        !isHiddenDay &&
        !isBefore(startOfDay(parsed), today)
      if (isBefore(startOfDay(parsed), today)) {
        setDateInvalidError(
          formatMessage({
            defaultMessage: 'Date is in the past',
            description: 'Error message for when provider tries to add availability for a date that is in the past',
          }),
        )
      }
      if (isHiddenDay) {
        const dayName = formatMessage(DAYS_OF_WEEK_SUNDAY_FIRST[dayOfWeekNum])
        setDateInvalidError(
          formatMessage(
            {
              defaultMessage: 'Availability cannot be added on a {dayName}',
              description: 'Error message for when provider tries to add availability on a day that is hidden',
            },
            { dayName },
          ),
        )
      }
      if (isFuture(parsed) && !isWithinInterval(parsed, { start: today, end: futureCutoff })) {
        setDateInvalidError(
          formatMessage({
            defaultMessage: 'Date is too far in the future',
            description:
              'Error message for when provider tries to add availability for a date that is too far in the future',
          }),
        )
      }
      setIsValidDateSelection(valid)
      onValidityChange?.(valid)
      if (valid && emitChange) onChange(formatted)
    },
    [formatMessage, localeDateFormat, hiddenDays, onChange, onValidityChange, useBuiltInValidation],
  )

  useEffect(() => {
    validate(value, false)
  }, [value, validate])

  const onRawChange = useCallback(
    ({ inputString, dateFormat }: { inputString: string; dateFormat: string }) => {
      if (!inputString) return
      if (!useBuiltInValidation) {
        onChange(inputString)
        return
      }

      const parsedDate = parse(inputString, dateFormat, new Date())
      if (isValid(parsedDate) && format(parsedDate, dateFormat) === inputString) {
        validate(format(parsedDate, localeDateFormat), true)
      }
    },
    [localeDateFormat, onChange, useBuiltInValidation, validate],
  )

  const onSelect = useCallback(
    (date: Date) => {
      validate(format(date, localeDateFormat), true)
    },
    [localeDateFormat, validate],
  )

  return (
    <DatePickerContainer style={style}>
      <DateField
        borderRadius={8}
        error={error ? error : isValidDateSelection || !useBuiltInValidation ? '' : (dateInvalidError ?? '')}
        label={formatMessage({
          defaultMessage: 'Date',
          description: 'Label for field where providers select the date they are adding availability for',
        })}
        readOnly={false}
        maxDate={addDays(new Date(), 90)}
        isClearable={false}
        input={{
          name: 'date',
          onChange: () => {},
          onBlur: () => onBlur?.(),
          onFocus: () => onFocus?.(),
          value: value ?? '',
        }}
        appendFormat={false}
        submitError={isValidDateSelection ? '' : (dateInvalidError ?? '')}
        locale={locale}
        onRawChange={onRawChange}
        onSelect={onSelect}
        inputStyle={{ marginBottom: 0 }}
        zIndex={10000}
        preventOpenOnFocus={preventOpenOnFocus}
      />
    </DatePickerContainer>
  )
}
